"use client"

import React, { useState, useEffect } from "react"
import { Link } from "react-router-dom"
import { Menu, X } from "lucide-react"

const navigationItems = [
  { name: "Home", href: "/#home", number: "01" },
  { name: "Prowess", href: "/#expertise", number: "02" },
  { name: "Work", href: "/#work-section", number: "03" },
  { name: "Experience", href: "/#experience-section", number: "04" },
  { name: "Connect", href: "/#contact", number: "05" },
]

export default function Header() {
  const [hoveredItem, setHoveredItem] = useState(null)
  const [isLoaded, setIsLoaded] = useState(false)
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)

  useEffect(() => {
    setIsLoaded(true)
  }, [])

  const handleNavigation = (e, href) => {
    e.preventDefault();
    const targetId = href.split('#')[1];
    const element = document.getElementById(targetId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setIsMobileMenuOpen(false);
  };

  // Close mobile menu when clicking outside or on escape
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape") {
        setIsMobileMenuOpen(false)
      }
    }

    const handleClickOutside = (e) => {
      const target = e.target
      if (!target.closest("[data-mobile-menu]") && !target.closest("[data-mobile-nav]")) {
        setIsMobileMenuOpen(false)
      }
    }

    if (isMobileMenuOpen) {
      document.addEventListener("keydown", handleEscape)
      document.addEventListener("click", handleClickOutside)
      document.body.style.overflow = "hidden" // Prevent background scroll
    } else {
      document.body.style.overflow = "unset"
    }

    return () => {
      document.removeEventListener("keydown", handleEscape)
      document.removeEventListener("click", handleClickOutside)
      document.body.style.overflow = "unset"
    }
  }, [isMobileMenuOpen])

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen)
  }

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false)
  }

  return (
    <>
      <header className="fixed top-0 left-0 right-0 z-[9999] bg-transparent backdrop-filter backdrop-blur-md border-b border-white/10">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-12 sm:h-14 lg:h-16">
            {/* Logo - Always visible */}
            <div className="flex-shrink-0 z-[9999]">
              <Link to="/" className="block">
                <div className="relative w-6 h-6 sm:w-8 sm:h-8 lg:w-10 lg:h-10">
                  <img
                    src="/logo.png"
                    alt="Logo"
                    className={`object-contain w-full h-full transition-transform duration-700 ease-in-out hover:animate-spin ${
                      isLoaded ? "animate-spin" : ""
                    }`}
                  />
                </div>
              </Link>
            </div>

            {/* Desktop Navigation - Hidden on mobile */}
            <nav
              className="hidden lg:block absolute left-1/2 transform -translate-x-1/2"
              role="navigation"
              aria-label="Main navigation"
            >
              <ul className="flex items-center space-x-4 xl:space-x-6">
                {navigationItems.map((item) => (
                  <li key={item.name}>
                    <Link
                      to={item.href}
                      onClick={(e) => handleNavigation(e, item.href)}
                      className={`group relative flex items-center space-x-1 py-1.5 px-2.5 rounded-lg transition-all duration-300 ease-in-out transform focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:ring-opacity-50 ${
                        hoveredItem === null
                          ? "text-white scale-100"
                          : hoveredItem === item.name
                            ? "text-yellow-400 scale-125"
                            : "text-gray-400 scale-90"
                      }`}
                      onMouseEnter={() => setHoveredItem(item.name)}
                      onMouseLeave={() => setHoveredItem(null)}
                      onFocus={() => setHoveredItem(item.name)}
                      onBlur={() => setHoveredItem(null)}
                    >
                      <span className="text-sm xl:text-base font-medium tracking-wide">{item.name}</span>
                      <span
                        className={`text-xs font-mono transition-colors duration-300 ${
                          hoveredItem === null
                            ? "text-gray-500"
                            : hoveredItem === item.name
                              ? "text-yellow-400"
                              : "text-gray-500"
                        }`}
                      >
                        {item.number}
                      </span>

                      {/* Hover indicator */}
                      <div
                        className={`absolute bottom-0 left-0 right-0 h-0.5 bg-yellow-400 transform origin-center transition-transform duration-300 ${
                          hoveredItem === item.name ? "scale-x-100" : "scale-x-0"
                        }`}
                      />
                    </Link>
                  </li>
                ))}
              </ul>
            </nav>

            {/* Mobile menu button */}
            <button
              onClick={toggleMobileMenu}
              className="lg:hidden z-[9999] p-1 rounded-lg text-white hover:text-yellow-400 hover:bg-white/10 transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:ring-opacity-50 flex items-center justify-center"
              aria-label="Toggle mobile menu"
              aria-expanded={isMobileMenuOpen}
              data-mobile-menu
            >
              {isMobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>

            {/* Spacer for desktop layout balance */}
            <div className="hidden lg:block flex-shrink-0 w-10" />
          </div>
        </div>

        {/* Mobile Navigation Menu */}
        <div
          className={`lg:hidden fixed inset-x-0 top-12 sm:top-14 bg-black/95 backdrop-filter backdrop-blur-md border-b border-white/10 transition-all duration-300 ease-in-out z-[9998] ${
            isMobileMenuOpen
              ? "opacity-100 translate-y-0 pointer-events-auto"
              : "opacity-0 -translate-y-full pointer-events-none"
          }`}
          data-mobile-nav
        >
          <nav className="container mx-auto px-4 sm:px-6 py-4" role="navigation" aria-label="Mobile navigation">
            <ul className="space-y-1">
              {navigationItems.map((item, index) => (
                <li key={item.name}>
                  <Link
                    to={item.href}
                    onClick={(e) => handleNavigation(e, item.href)}
                    className="group flex items-center justify-between py-3 px-4 rounded-lg text-white hover:text-yellow-400 hover:bg-white/5 transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:ring-opacity-50 focus:bg-white/5"
                    style={{
                      animationDelay: `${index * 50}ms`,
                      animation: isMobileMenuOpen ? "slideInFromTop 0.3s ease-out forwards" : "none",
                    }}
                  >
                    <div className="flex items-center space-x-3">
                      <span className="text-base font-medium tracking-wide">{item.name}</span>
                    </div>
                    <span className="text-sm font-mono text-gray-400 group-hover:text-yellow-400 transition-colors duration-300">
                      {item.number}
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          </nav>
        </div>
      </header>

      {/* Mobile menu overlay */}
      {isMobileMenuOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/50 z-[9997] transition-opacity duration-300"
          onClick={closeMobileMenu}
          aria-hidden="true"
        />
      )}

      {/* Animation styles */}
      <style>
        {`
          @keyframes slideInFromTop {
            from {
              opacity: 0;
              transform: translateY(-20px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
        `}
      </style>
    </>
  )
}
